import React, { useCallback, useEffect, useState } from 'react';
import {
  ExecutionTemplate,
  ExecutionTemplateType,
  ExecutionTemplateCategory,
  ExecutionPipelinesApiClient,
  resolveExecutionTemplateTriggerMode,
} from 'process-pipelines-common';

export interface BasicsTabProps {
  template: ExecutionTemplate;
  onChange: (updates: Partial<ExecutionTemplate>) => void;
  readOnly?: boolean;
  readOnlyScope?: boolean; // When creating from Studio, scope is read-only
  apiClient?: ExecutionPipelinesApiClient; // Used to populate Type/Category dropdowns
}

/** `my-template-name` from `My Template Name` -- same simple slugify App Studio's PagesPanel.tsx
 *  and @marketplace/common-js's formatters.ts already use, not a new convention invented here. */
function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export const BasicsTab: React.FC<BasicsTabProps> = ({
  template,
  onChange,
  readOnly = false,
  readOnlyScope = false,
  apiClient,
}) => {
  // Lookup data for the Type / Category dropdowns (Process_ExecutionTemplateTypes / Process_ExecutionTemplateCategories)
  const [templateTypes, setTemplateTypes] = useState<ExecutionTemplateType[]>([]);
  const [templateCategories, setTemplateCategories] = useState<ExecutionTemplateCategory[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [lookupsError, setLookupsError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiClient) return;
    let cancelled = false;

    setLookupsLoading(true);
    setLookupsError(null);

    Promise.all([
      apiClient.getEnabledExecutionTemplateTypes(),
      apiClient.getEnabledExecutionTemplateCategories(),
    ])
      .then(([types, categories]) => {
        if (cancelled) return;
        setTemplateTypes([...types].sort((a, b) => a.displayOrder - b.displayOrder));
        setTemplateCategories([...categories].sort((a, b) => a.displayOrder - b.displayOrder));
      })
      .catch((err) => {
        if (cancelled) return;
        setLookupsError(err instanceof Error ? err.message : 'Failed to load template types/categories');
      })
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // Ensure concurrencyBehavior has a default value if missing
  useEffect(() => {
    if (!template.concurrencyBehavior) {
      onChange({ concurrencyBehavior: 'Allow' });
    }
  }, []);

  // Ensure defaultTriggerMode has a default value if missing ('Execute', matching the DB default of 1).
  // Sent to the API as the enum's string member name -- this host's global JsonStringEnumConverter
  // (see PlatformWebServerExtensionsByBuilder.cs) serializes/expects enum-typed properties this way,
  // as confirmed by GET responses returning e.g. "ExecuteAndTrack" rather than a bare number.
  useEffect(() => {
    if (!template.defaultTriggerMode) {
      onChange({ defaultTriggerMode: 'Execute' });
    }
  }, []);

  const handleChange = useCallback(
    (field: keyof ExecutionTemplate, value: any) => {
      onChange({ [field]: value });
    },
    [onChange]
  );

  const createInputHandler = (field: keyof ExecutionTemplate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    handleChange(field, e.target.value);
  };

  // Auto-derive Slug from Name while Slug is still untouched/derived -- same "type the human
  // label, get the machine identifier for free" convention already used by App Studio's
  // PagesPanel.tsx (handleTitleChange) and CreateAppModal's AppCode derivation. Slug is a
  // required field (Process_ExecutionTemplates.Slug) with no server-side default, and this tab
  // previously left it fully blank until the user manually typed one -- easy to miss since it
  // sits in a secondary row below Name, and its absence surfaced only as a client-side "Slug is
  // required" validation error on Save (TemplateEditor.tsx's handleSave). Never clobbers a slug
  // the user already edited by hand.
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      const wasAutoSlug = !template.slug || template.slug === slugify(template.name || '');
      onChange(
        wasAutoSlug ? { name, slug: slugify(name) } : { name }
      );
    },
    [onChange, template.name, template.slug]
  );

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <label style={styles.label}>Name <span style={{...styles.asterisk, color: 'rgb(239, 68, 68)', WebkitTextFillColor: 'rgb(239, 68, 68)'}}>*</span></label>
        <input
          type="text"
          value={template.name}
          onChange={handleNameChange}
          style={styles.input}
          disabled={readOnly}
          placeholder="Template name"
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Description</label>
        <textarea
          value={template.description || ''}
          onChange={createInputHandler('description')}
          style={styles.textarea}
          disabled={readOnly}
          placeholder="Short description"
          rows={2}
        />
      </div>

      <div style={styles.row}>
        <div style={styles.colHalf}>
          <label style={styles.label}>Type</label>
          <select
            value={template.executionTemplateTypeID ?? ''}
            onChange={(e) =>
              handleChange('executionTemplateTypeID', e.target.value === '' ? null : Number(e.target.value))
            }
            style={{ ...styles.input, ...styles.select }}
            disabled={readOnly || lookupsLoading}
          >
            <option value="">{lookupsLoading ? 'Loading types...' : '(No type)'}</option>
            {templateTypes.map((t) => (
              <option key={t.executionTemplateTypeID} value={t.executionTemplateTypeID}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.colHalf}>
          <label style={styles.label}>Category</label>
          <select
            value={template.executionTemplateCategoryID ?? ''}
            onChange={(e) =>
              handleChange('executionTemplateCategoryID', e.target.value === '' ? null : Number(e.target.value))
            }
            style={{ ...styles.input, ...styles.select }}
            disabled={readOnly || lookupsLoading}
          >
            <option value="">{lookupsLoading ? 'Loading categories...' : '(No category)'}</option>
            {templateCategories.map((c) => (
              <option key={c.executionTemplateCategoryID} value={c.executionTemplateCategoryID}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {lookupsError && (
        <p style={{ ...styles.helperText, color: '#ef4444' }}>
          Could not load Type/Category options: {lookupsError}
        </p>
      )}

      <div style={styles.section}>
        <label style={styles.label}>Long Description</label>
        <textarea
          value={template.longDescription || ''}
          onChange={createInputHandler('longDescription')}
          style={styles.textarea}
          disabled={readOnly}
          placeholder="Detailed description"
          rows={3}
        />
      </div>

      <div style={styles.row}>
        <div style={styles.colHalf}>
          <label style={styles.label}>Display Name</label>
          <input
            type="text"
            value={template.displayName || ''}
            onChange={createInputHandler('displayName')}
            style={styles.input}
            disabled={readOnly}
            placeholder="Display name"
          />
        </div>
        <div style={styles.colHalf}>
          <label style={styles.label}>Slug <span style={{...styles.asterisk, color: 'rgb(239, 68, 68)', WebkitTextFillColor: 'rgb(239, 68, 68)'}}>*</span></label>
          <input
            type="text"
            value={template.slug || ''}
            onChange={createInputHandler('slug')}
            style={styles.input}
            disabled={readOnly}
            placeholder="URL-friendly slug"
            required
          />
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.colHalf}>
          <label style={styles.label}>Icon URL</label>
          <input
            type="text"
            value={template.iconUrl || ''}
            onChange={createInputHandler('iconUrl')}
            style={styles.input}
            disabled={readOnly}
            placeholder="Icon URL"
          />
        </div>
        <div style={styles.colHalf}>
          <label style={styles.label}>Image URL</label>
          <input
            type="text"
            value={template.imageUrl || ''}
            onChange={createInputHandler('imageUrl')}
            style={styles.input}
            disabled={readOnly}
            placeholder="Image URL"
          />
        </div>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Concurrency Behavior <span style={{...styles.asterisk, color: 'rgb(239, 68, 68)', WebkitTextFillColor: 'rgb(239, 68, 68)'}}>*</span></label>
        <select
          value={template.concurrencyBehavior || 'Allow'}
          onChange={(e) => handleChange('concurrencyBehavior', e.target.value as 'Allow' | 'Block')}
          style={{...styles.input, ...styles.select}}
          disabled={readOnly}
        >
          <option value="Allow">Allow (concurrent executions permitted)</option>
          <option value="Block">Block (prevent concurrent executions)</option>
        </select>
        <p style={styles.helperText}>
          Choose how this template handles concurrent executions from multiple users or processes.
        </p>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Default Trigger Mode <span style={{...styles.asterisk, color: 'rgb(239, 68, 68)', WebkitTextFillColor: 'rgb(239, 68, 68)'}}>*</span></label>
        <select
          value={resolveExecutionTemplateTriggerMode(template.defaultTriggerMode)}
          onChange={(e) => handleChange('defaultTriggerMode', e.target.value)}
          style={{...styles.input, ...styles.select}}
          disabled={readOnly}
        >
          <option value="Execute">Execute</option>
          <option value="ExecuteAndTrack">Execute and Track</option>
          <option value="ChatNow">Chat Now</option>
        </select>
        <p style={styles.helperText}>
          Choose what happens by default when a user triggers this template: run it in the background
          (Execute), run it with live progress tracking (Execute and Track), or open it in a new chat
          window (Chat Now).
        </p>
      </div>

      {readOnlyScope && (
        <div style={styles.section}>
          <label style={styles.label}>Scope (Read-Only)</label>
          <div style={styles.readOnlyScopeDisplay}>
            <span><strong>Type:</strong> {template.executionScope}</span>
            <span><strong>Reference ID:</strong> {template.scopeReferenceID}</span>
          </div>
          <p style={styles.helperText}>
            Scope is set when creating this template and cannot be changed here.
          </p>
        </div>
      )}

      {readOnly && (
        <div style={styles.readOnlyBanner}>
          This template is in read-only mode. Edit is not allowed.
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '16px',
  },
  section: {
    marginBottom: '16px',
  },
  row: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  colHalf: {
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600' as const,
    fontSize: '13px',
    color: 'var(--text-primary, #e5e7eb)',
  },
  asterisk: {
    fontWeight: 'bold' as const,
    display: 'inline',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
    color: 'var(--text-primary, #e5e7eb)',
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
    color: 'var(--text-primary, #e5e7eb)',
  },
  select: {
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23e5e7eb' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '1.5em',
    paddingRight: '32px',
  },
  helperText: {
    margin: '6px 0 0 0',
    fontSize: '12px',
    color: 'var(--text-secondary, #9ca3af)',
  },
  readOnlyBanner: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    borderRadius: '4px',
    fontSize: '12px',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  readOnlyScopeDisplay: {
    padding: '10px',
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.06))',
    borderRadius: '4px',
    fontSize: '13px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    color: 'var(--text-primary, #e5e7eb)',
  },
} as const;
